import { useState, useEffect } from "react";
import { fetchApi } from "@/services/apiService";

export type WhatsAppMessage = {
  id: number | string;
  direction: "incoming" | "outgoing";
  text: string;
  time: string;
  read: boolean;
};

export type WhatsAppConversation = {
  id: number | string;
  leadId?: number | string;
  name: string;
  company?: string;
  phone: string;
  avatar: string;
  avatarBg?: string;
  avatarTextColor?: string;
  unread: number;
  lastMessage: string;
  lastMessageTime: string;
  assigned?: string;
  assignedAgentId?: string;
  assignedAgentName?: string;
  assignedAgentInitials?: string;
  assignedAgentActive?: boolean;
  stage?: string;
  source?: string;
  sourceColor?: string;
  email?: string;
  value?: string;
  dealValue?: string;
  reassignedFrom?: string | null;
  reassignReason?: string | null;
  assignmentHistory?: {
    fromAgent: string;
    toAgent: string;
    reason: string;
    timestamp: string;
  }[];
  messages: WhatsAppMessage[];
  notes?: string;
  tags?: string[];
};

type FilterTab = "all" | "mine" | "unassigned" | "reassigned";

interface Props {
  conversations: WhatsAppConversation[];
  selectedId: number | string;
  onSelect: (conversation: WhatsAppConversation) => void;
  onManageClick: () => void;
  onStartLeadChat?: (lead: any) => void;
}

const chatFilterPills: { key: FilterTab; label: string }[] = [
  { key: "all", label: "All Chats" },
  { key: "mine", label: "Mine" },
  { key: "unassigned", label: "Unassigned" },
  { key: "reassigned", label: "Reassigned" },
];

function getUnassignedCount(conversations: WhatsAppConversation[]): number {
  return conversations.filter((c) => !c.assignedAgentName || c.assignedAgentName === "Unassigned" || !c.assignedAgentId).length;
}

export default function ConversationList({ conversations, selectedId, onSelect, onManageClick, onStartLeadChat }: Props) {
  const [activeChatFilter, setActiveChatFilter] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [leadsList, setLeadsList] = useState<any[]>([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [loadingLeads, setLoadingLeads] = useState(false);

  const currentAgentName = localStorage.getItem("user_name") || "Rajesh Kumar";
  const unassignedCount = getUnassignedCount(conversations);

  useEffect(() => {
    if (showNewChatModal && leadsList.length === 0) {
      setLoadingLeads(true);
      fetchApi<any[]>('/leads', undefined, [])
        .then((res) => {
          const items = Array.isArray(res) ? res : (res as any)?.data || (res as any)?.items || [];
          setLeadsList(items);
        })
        .catch(() => {})
        .finally(() => setLoadingLeads(false));
    }
  }, [showNewChatModal, leadsList.length]);

  const filteredLeads = leadsList.filter((l) => {
    if (!leadSearch.trim()) return true;
    const q = leadSearch.toLowerCase();
    return (
      (l.name && l.name.toLowerCase().includes(q)) ||
      (l.company && l.company.toLowerCase().includes(q)) ||
      (l.phone && l.phone.includes(q))
    );
  });

  const filtered = conversations.filter((c) => {
    const isUnassigned = !c.assignedAgentName || c.assignedAgentName === "Unassigned";
    if (activeChatFilter === "mine" && c.assignedAgentName !== currentAgentName && c.assigned !== currentAgentName) return false;
    if (activeChatFilter === "unassigned" && !isUnassigned) return false;
    if (activeChatFilter === "reassigned" && !c.reassignedFrom && (!c.assignmentHistory || c.assignmentHistory.length === 0)) return false;

    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        (c.lastMessage && c.lastMessage.toLowerCase().includes(q)) ||
        (c.phone && c.phone.includes(q)) ||
        (c.company && c.company.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const getAgentAvatar = (conv: WhatsAppConversation) => {
    const name = conv.assignedAgentName || conv.assigned;
    if (!name || name === "Unassigned") return null;
    const initials = conv.assignedAgentInitials || name.split(" ").map((n) => n[0]).join("").toUpperCase();
    return {
      bg: "bg-indigo-100 text-indigo-700",
      initials,
      name,
    };
  };

  const getContactInitials = (name: string, avatar?: string) => {
    if (avatar && avatar.length <= 3) return avatar;
    if (!name) return "C";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const [metaInfo, setMetaInfo] = useState<{ display_phone_number?: string; verified_name?: string; error?: string } | null>(null);

  useEffect(() => {
    fetchApi<any>('/whatsapp/debug-meta', undefined, null)
      .then((res) => {
        if (res?.phoneData?.display_phone_number) {
          setMetaInfo({
            display_phone_number: res.phoneData.display_phone_number,
            verified_name: res.phoneData.verified_name,
          });
        } else if (res?.phoneData?.error) {
          setMetaInfo({
            error: res.phoneData.error.message || 'Meta Phone ID Error',
          });
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="w-80 flex-shrink-0 flex flex-col border-r border-[#e5e7eb] bg-white">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb]">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]">WhatsApp Inbox</h3>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`w-2 h-2 rounded-full ${metaInfo?.error ? 'bg-amber-500' : 'bg-green-500'}`} />
            <span className={`text-xs font-medium ${metaInfo?.error ? 'text-amber-600' : 'text-green-600'}`} title={metaInfo?.error}>
              {metaInfo?.error
                ? 'Meta Config Warning'
                : metaInfo?.display_phone_number
                ? `${metaInfo.display_phone_number}`
                : 'Connected'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowNewChatModal(true)}
            title="Start chat with a CRM lead"
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center gap-1 cursor-pointer shadow-sm"
          >
            <i className="ri-chat-new-line text-sm" />
            <span>New</span>
          </button>
          <button
            onClick={onManageClick}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-settings-3-line mr-1" />
            Manage
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations or company..."
            className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-lg text-sm text-[#111827] placeholder-[#9ca3af] border-none focus:outline-none focus:ring-1 focus:ring-indigo-300"
          />
        </div>
      </div>

      {/* Chat filter pills */}
      <div className="flex items-center gap-1.5 px-3 pb-2">
        {chatFilterPills.map((pill) => (
          <button
            key={pill.key}
            onClick={() => setActiveChatFilter(pill.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
              activeChatFilter === pill.key
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {pill.label}
            {pill.key === "unassigned" && unassignedCount > 0 && (
              <span className={`ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-semibold ${
                activeChatFilter === "unassigned" ? "bg-white/30 text-white" : "bg-red-100 text-red-600"
              }`}>
                {unassignedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
              <i className="ri-message-3-line text-3xl text-green-300" />
            </div>
            <p className="text-base font-semibold text-gray-700 mb-1">No conversations yet</p>
            <p className="text-sm text-gray-500 max-w-xs">
              WhatsApp conversations will appear here when leads message you
            </p>
          </div>
        )}
        {filtered.map((conv) => {
          const isActive = String(conv.id) === String(selectedId);
          const agent = getAgentAvatar(conv);
          const initials = getContactInitials(conv.name, conv.avatar);
          return (
            <button
              key={conv.id}
              onClick={() => onSelect(conv)}
              className={`w-full text-left px-4 py-3 border-b border-[#f1f5f9] hover:bg-gray-50 transition-colors cursor-pointer ${
                isActive ? "bg-indigo-50 border-l-4 border-l-indigo-600" : "border-l-4 border-l-transparent"
              }`}
            >
              <div className="flex gap-3">
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center flex-shrink-0 font-semibold text-sm`}>
                  <span>{initials}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#111827] truncate">{conv.name}</span>
                    <span className="text-xs text-[#9ca3af] flex-shrink-0 ml-2 whitespace-nowrap">
                      {conv.lastMessageTime}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-[#6b7280] truncate">
                      {conv.company ? <span className="text-[#4b5563] font-medium mr-1">[{conv.company}]</span> : null}
                      {conv.lastMessage || "No messages yet"}
                    </span>
                    {conv.unread > 0 && (
                      <span className="bg-green-500 text-white text-xs rounded-full px-1.5 py-px font-medium flex-shrink-0 ml-2 min-w-[20px] text-center">
                        {conv.unread}
                      </span>
                    )}
                  </div>

                  {/* Agent indicator & Stage */}
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {agent ? (
                        <>
                          <div className={`w-4 h-4 rounded-full ${agent.bg} flex items-center justify-center flex-shrink-0`}>
                            <span className="text-[8px] font-bold">{agent.initials}</span>
                          </div>
                          <span className="text-xs text-[#9ca3af] truncate">{agent.name}</span>
                        </>
                      ) : (
                        <span className="text-xs text-red-500 font-medium">Unassigned</span>
                      )}
                      {conv.reassignedFrom && (
                        <span className="text-[10px] text-amber-600 flex-shrink-0" title={`Reassigned from ${conv.reassignedFrom}`}>↻</span>
                      )}
                    </div>
                    {conv.stage && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium truncate max-w-[100px]">
                        {conv.stage}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Start Chat with CRM Lead Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-slide-in">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-base font-bold text-gray-900">Start WhatsApp Chat</h3>
                <p className="text-xs text-gray-500">Select a lead from your CRM database</p>
              </div>
              <button
                onClick={() => setShowNewChatModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Search leads by name, phone or company..."
                  className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 border-none focus:outline-none focus:ring-2 focus:ring-green-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 divide-y divide-gray-50">
              {loadingLeads ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  <i className="ri-loader-4-line animate-spin text-lg block mb-2" />
                  Loading CRM leads...
                </div>
              ) : filteredLeads.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  <i className="ri-user-unfollow-line text-2xl text-gray-300 block mb-2" />
                  No leads found matching your search.
                </div>
              ) : (
                filteredLeads.map((lead) => {
                  const leadInitials = (lead.name || "L")
                    .split(" ")
                    .map((n: string) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);
                  return (
                    <button
                      key={lead.id}
                      onClick={() => {
                        setShowNewChatModal(false);
                        if (onStartLeadChat) {
                          onStartLeadChat(lead);
                        }
                      }}
                      className="w-full text-left p-3 hover:bg-green-50/60 rounded-xl transition-colors flex items-center gap-3 cursor-pointer group"
                    >
                      <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-xs flex-shrink-0 group-hover:bg-green-600 group-hover:text-white transition-colors">
                        {leadInitials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-900 truncate">{lead.name}</p>
                          {lead.stage && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium flex-shrink-0 ml-1">
                              {lead.stage}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          {lead.company && <span className="truncate">{lead.company} &middot;</span>}
                          <span className="text-green-700 font-medium">{lead.phone || "No phone"}</span>
                        </div>
                      </div>
                      <i className="ri-chat-1-line text-gray-300 group-hover:text-green-600 text-lg flex-shrink-0" />
                    </button>
                  );
                })
              )}
            </div>

            <div className="p-3 bg-gray-50 border-t border-gray-100 text-center">
              <span className="text-xs text-gray-500">
                Found {filteredLeads.length} lead{filteredLeads.length === 1 ? "" : "s"} in CRM
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}